/**
 * Etapa 4: Preview das variáveis importadas
 * Mostra os 10 primeiros registros com todas as colunas de variáveis
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  Variable,
  ChevronRight,
  AlertTriangle,
  Hash
} from 'lucide-react';

import { CSVParseResult, RowValidation, extractVariableColumns } from '@/lib/csv-utils';
import { ImportResult } from '@/hooks/useListImport';

// =====================================================
// TIPOS
// =====================================================

interface VariablesPreviewStepProps {
  csvData: CSVParseResult | null;
  validatedRows: RowValidation[];
  importResult: ImportResult | null;
  onComplete: () => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function VariablesPreviewStep({
  csvData,
  validatedRows,
  importResult,
  onComplete
}: VariablesPreviewStepProps) {
  if (!csvData || !importResult) {
    return null;
  }
  
  const variableColumns = extractVariableColumns(csvData.headers);
  const previewRows = validatedRows.filter(v => v.isValid && !v.isDuplicate).slice(0, 10);
  
  return (
    <div 
      className="space-y-6 w-full" 
      style={{ 
        maxWidth: '100%', 
        overflow: 'hidden', 
        contain: 'inline-size' 
      }}
    >
      {/* Resumo da importação */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                <p className="text-xs text-muted-foreground">Importados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{importResult.updated}</p>
                <p className="text-xs text-muted-foreground">Atualizados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-orange-600">{importResult.skipped}</p>
                <p className="text-xs text-muted-foreground">Ignorados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Variáveis disponíveis */}
      {variableColumns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Variable className="h-5 w-5" />
              Variáveis Disponíveis
            </CardTitle>
            <CardDescription>
              Use estas variáveis nas mensagens da campanha
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Container com scroll horizontal para muitas variáveis */}
            <div 
              className="relative overflow-hidden" 
              style={{ maxWidth: '100%', width: '100%' }}
            >
              {/* Gradiente de fade à direita indicando mais conteúdo */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              
              <div 
                className="flex items-center gap-2 pb-2 pr-8 overflow-x-auto overflow-y-hidden scrollbar-thin"
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth'
                }}
              >
                <Badge variant="secondary" className="text-sm shrink-0">
                  <Hash className="h-3 w-3 mr-1" />
                  {'{{nome}}'}
                </Badge>
                <Badge variant="secondary" className="text-sm shrink-0">
                  <Hash className="h-3 w-3 mr-1" />
                  {'{{telefone}}'}
                </Badge>
                {variableColumns.map(col => (
                  <Badge key={col} variant="outline" className="text-sm shrink-0">
                    <Hash className="h-3 w-3 mr-1" />
                    {`{{${col}}}`}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Preview dos dados - com containment robusto */}
      <Card>
        <CardHeader>
          <CardTitle>Preview dos Dados (primeiros 10)</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Container com scroll horizontal contido */}
          <div 
            className="relative" 
            style={{ 
              width: '100%', 
              maxWidth: '100%', 
              overflow: 'hidden' 
            }}
          >
            {/* Gradiente de fade à direita indicando mais conteúdo */}
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
            
            <div 
              className="border rounded-md overflow-auto scrollbar-thin"
              style={{ 
                width: '100%',
                maxWidth: '100%',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {/* Wrapper interno com largura baseada nas colunas */}
              <div style={{ minWidth: `${Math.max((variableColumns.length + 3) * 150, 400)}px` }}>
                <ScrollArea className="h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 whitespace-nowrap">#</TableHead>
                        <TableHead className="whitespace-nowrap">Nome</TableHead>
                        <TableHead className="whitespace-nowrap">Telefone</TableHead>
                        {variableColumns.map(col => (
                          <TableHead key={col} className="whitespace-nowrap">
                            <Badge variant="outline" className="font-normal">
                              {col}
                            </Badge>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.map((validation, index) => (
                        <TableRow key={validation.rowIndex}>
                          <TableCell className="text-muted-foreground whitespace-nowrap">{index + 1}</TableCell>
                          <TableCell className="whitespace-nowrap">{validation.row.nome || '-'}</TableCell>
                          <TableCell className="font-mono text-sm whitespace-nowrap">
                            {validation.phoneNormalized || validation.row.telefone}
                          </TableCell>
                          {variableColumns.map(col => (
                            <TableCell key={col} className="max-w-[200px] truncate whitespace-nowrap">
                              {validation.row[col] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Erros, se houver */}
      {importResult.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Alguns erros ocorreram:</strong>
            <ul className="mt-2 list-disc list-inside">
              {importResult.errors.slice(0, 5).map((error, i) => (
                <li key={i} className="text-sm">{error}</li>
              ))}
              {importResult.errors.length > 5 && (
                <li className="text-sm">... e mais {importResult.errors.length - 5} erros</li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Botão de continuar */}
      <div className="flex justify-end">
        <Button onClick={onComplete}>
          Continuar
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
